<template>
  <ErrorBoundary module-name="articles-feed-item">
    <div
      :class="feedItemClass"
      class="articles-feed-item"
    >
      <ArticleCardSmallList
        class="articles-feed-item__list"
        :articles="item"
        :meta="archiveMeta"
      />
      <WidgetsAreaHome
        v-if="widgetsByIndex"
        class="articles-feed-item__widgets-area"
        :widgets="widgetsByIndex"
      />
      <IntersectionObserver
        :once="true"
        @appear="sendAppearEvent"
      />
    </div>
  </ErrorBoundary>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { TArticle, TContent } from '~/types/common';
  import ErrorBoundary from '@devhacker/shared/components/ErrorBoundary.vue';
  import ArticleCardSmallList from '~/components/ArticleCardSmallList.vue';
  import IntersectionObserver from '~/components/IntersectionObserver.vue';
  import WidgetsAreaHome from '~/components/WidgetsArea/WidgetsAreaHome.vue';
  import type { IArticlesConfig } from '~/types/pages';
  import { useWidgetsStore } from '~/store/widgets';
  import { useArchivesStore } from '~/store/archives';

  export default defineNuxtComponent({
    name: 'ArticlesFeedItem',
    components: {
      ErrorBoundary,
      ArticleCardSmallList,
      IntersectionObserver,
      WidgetsAreaHome,
    },

    props: {
      item: {
        type: Array as PropType<TArticle[]>,
        required: true,
      },

      loopWidgets: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      index: {
        type: Number as PropType<number>,
        required: true,
      },

      articlesConfig: {
        type: Object as PropType<IArticlesConfig>,
        required: true,
      },

      articlesFeed: {
        type: Array as PropType<TArticle[][]>,
        required: true,
      },
    },

    computed: {
      archiveMeta() {
        return useArchivesStore().meta;
      },

      widgets() {
        return useWidgetsStore().homeWidgets;
      },

      isFirstPage(): boolean {
        return this.index + 1 === 1;
      },

      currentPage(): number {
        return this.articlesConfig.pack.page;
      },

      isFull(): boolean {
        return this.currentPage >= this.maxPages;
      },

      maxPages(): number {
        return this.articlesConfig.pack.maxPages;
      },

      analyticsSize(): number {
        return this.articlesConfig.chunk.analyticsSize;
      },

      chunkSize(): number {
        return this.articlesConfig.chunk.size;
      },

      feedItemClass(): Record<string, any> {
        const className = 'articles-feed-item';
        return {
          [`${className}--widgets-area-empty`]: !this.widgetsByIndex,
          [`${className}--prev-widgets-area-empty`]: this.prevWidgetsAreaEmpty,
          [`${className}--last`]: this.isLastItem,
        };
      },

      isLastItem(): boolean {
        const { isFull, articlesFeed, index } = this;
        return isFull && index === articlesFeed.length - 1;
      },

      widgetsByIndex(): TContent[] | null {
        const { widgetByIndex } = this;

        return widgetByIndex ? [widgetByIndex] : null;
      },

      prevWidgetsAreaEmpty(): boolean {
        return this.loopWidgets
          ? this.widgets.length === 0
          : !this.widgets[this.index - 1] && !this.isFirstPage;
      },

      widgetByIndex(): TContent | null {
        const { widgets, index, loopWidgets } = this;

        return loopWidgets ? widgets[index % widgets.length] : (widgets[index] ?? null);
      },

      articlesCount(): number {
        return this.index * this.chunkSize;
      },

      isShowObserver(): boolean {
        return (this.index + 1) % (this.analyticsSize / this.chunkSize) === 0;
      },
    },

    methods: {
      sendAppearEvent() {
        if (this.isShowObserver) {
          this.$emit('load-articles');
        }
        this.$emit('send-analytics', this.articlesCount);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .articles-feed-item {
    &__list {
      background: #ffffff;
      margin: 0 2px;
      border-radius: 32px;
      @include tablet {
        margin: 0;
      }
      @include desktop {
        max-width: 828px;
      }
      @include desktop {
        margin: 0;
      }

      + .articles-feed-item__widgets-area {
        margin-top: $spacing-8;
        @include tablet {
          margin-top: $spacing-4;
        }
        @include big-tablet {
          margin-top: $spacing-6;
        }
      }
    }

    &__widgets-area {
      padding: $spacing-8 0;
      @include tablet {
        padding: 0;
      }
      @include desktop {
        margin-right: $spacing-10;
      }
    }
  }

  .articles-feed-item--widgets-area-empty {
    .articles-feed-item {
      &__list {
        border-radius: 32px 32px 0 0;
        position: relative;

        &::after {
          position: absolute;
          content: '';
          width: 100%;
          height: 1px;
          bottom: 0;
          left: 0;
          background-color: $stroke-color--1;
          @include tablet {
            width: calc(100% - 45px);
            left: 0;
            right: 0;
            margin: 0 auto;
          }
          @include big-tablet {
            width: calc(100% - 65px);
          }
        }
      }
    }
  }

  .articles-feed-item--prev-widgets-area-empty {
    .articles-feed-item {
      &__list {
        border-radius: 0;
      }
    }
  }

  .articles-feed-item--last {
    .articles-feed-item {
      &__list {
        &::after {
          display: none;
        }
      }
    }
  }
</style>
